import './assets/css/App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './layouts/admin';
import AgencyLayout from './layouts/agency';
import TrainerLayout from './layouts/trainer';
import AuthLayout from './layouts/auth';
import NotFound from './views/common/NotFound';
import PublicGettingStarted from './views/public/gettingStarted';
import { useAuth } from './contexts/AuthContext';
import { getDefaultDashboard } from './routes';
import {
  ChakraProvider,
  Flex,
  Spinner,
  Text,
  VStack,
  // extendTheme
} from '@chakra-ui/react';
import initialTheme from './theme/theme'; //  { themeGreen }
import { useState } from 'react';
// Chakra imports

export default function Main() {
  // eslint-disable-next-line
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <ChakraProvider theme={currentTheme}>
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="100vh"
          bg="gray.50"
          _dark={{ bg: "gray.900" }}
        >
          <VStack spacing={6}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
            <Text
              fontSize="lg"
              fontWeight="medium"
              color="gray.600"
              _dark={{ color: "gray.300" }}
            >
              Loading your dashboard...
            </Text>
          </VStack>
        </Flex>
      </ChakraProvider>
    );
  }

  // Protected Route Component with Role-based routing
  const ProtectedRoute = ({ children, allowedRoles }: { children: React.ReactNode, allowedRoles?: string[] }) => {
    if (!isAuthenticated) {
      return <Navigate to="/auth/sign-in" replace />;
    }

    // Check if user has required role
    if (allowedRoles && user?.role && !allowedRoles.includes(user.role)) {
      // Redirect to user's default dashboard
      return <Navigate to={getDefaultDashboard(user.role)} replace />;
    }

    return <>{children}</>;
  };

  // Public Route Component (redirect to user's dashboard if already authenticated)
  const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    if (isAuthenticated && user?.role) {
      return <Navigate to={getDefaultDashboard(user.role)} replace />;
    }
    return <>{children}</>;
  };

  return (
    <ChakraProvider theme={currentTheme}>
      <Routes>
        <Route
          path="auth/*"
          element={
            <PublicRoute>
              <AuthLayout />
            </PublicRoute>
          }
        />
        <Route
          path="admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />
            </ProtectedRoute>
          }
        />
        <Route
          path="agency/*"
          element={
            <ProtectedRoute allowedRoles={['agency']}>
              <AgencyLayout theme={currentTheme} setTheme={setCurrentTheme} />
            </ProtectedRoute>
          }
        />
        <Route
          path="trainer/*"
          element={
            <ProtectedRoute allowedRoles={['trainer']}>
              <TrainerLayout theme={currentTheme} setTheme={setCurrentTheme} />
            </ProtectedRoute>
          }
        />
        {/* Public Getting Started Route */}
        <Route
          path="/getting-started"
          element={<PublicGettingStarted />}
        />
        <Route
          path="/"
          element={
            isAuthenticated && user?.role ? (
              <Navigate to={getDefaultDashboard(user.role)} replace />
            ) : (
              <Navigate to="/auth/sign-in" replace />
            )
          }
        />
        {/* 404 page for any routes that don't match */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ChakraProvider>
  );
}
